import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { BaseStitchListComponent, type StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { SheetComponent } from '@modules/elements/components/sheet/sheet.component';
import { SelectableListDirective } from '@modules/drag-n-drop/directives/selectable-list.directive';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { SheetsAppStateService } from '@modules/sheets/services/state.service';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { Sheet } from '@modules/sheets/types/sheet';
import { SheetsFilters } from '@modules/sheets/types/sheets-filters';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-sheets-list',
  templateUrl: './sheets-list.component.html',
  styleUrls: ['./sheets-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class SheetsListComponent
  extends BaseStitchListComponent<Sheet>
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  /** Show per-row sheet-folder-location badges (search results, where results span sheet-folders). */
  public readonly showLocationBadge = input<boolean>(false);

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** The list beside an open workbook (SBUX-1145's list toggle), where a selection opens its workbook in the editor. A
   * workbook leaving this list only leaves it: the selection does not move on to the next workbook, which would switch
   * the editor to it. */
  public readonly besideEditor = input(false);

  /** Container treatment forwarded to every card; the knowledge hub asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The hub draws its related items as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  /** NFR-8: the list's name, among the template's other words, comes from the Sheets strings. */
  protected readonly S = SHEETS_STRINGS;

  @Input()
  public override filters: Partial<SheetsFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickSheet = new EventEmitter<Sheet>();
  @Output()
  public readonly openSheet = new EventEmitter<Sheet>();
  @Output()
  public readonly doubleClickSheet = new EventEmitter<Sheet>();

  public items: StitchListItem<Sheet>[] = [];

  @ViewChildren(SheetComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  @ViewChild(SelectableListDirective)
  private selection: SelectableListDirective<Sheet>;

  private readonly sheetsService = inject(SheetsService);
  private readonly linkedInfoService = inject(LinkedInfoService);
  private readonly sheetsStateService = inject(SheetsAppStateService);

  protected override getStitchType(): StitchType {
    return StitchType.SHEET;
  }

  public override ngOnInit() {
    merge(this.linkedInfoService.getRefresh(), this.sheetsService.getRefresh())
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });


    super.ngOnInit();
  }

  public ngAfterViewInit(): void {
    // Beside the editor the selection that moved on is dropped as it lands, before it paints. That also cancels the
    // open the list queued for it: a pending open belongs to a selected row.
    this.selection.selectableService
      .getAdvanced()
      .pipe(
        filter((target) => target !== null && this.besideEditor()),
        takeUntil(this.alive),
      )
      .subscribe(() => this.resetSelected.next());
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes) {
      this.resetItems();
    }
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: Sheet[]; count: number }> {
    return this.sheetsService.search(
      {
        ...this.filters,
        archived: this.filters?.archived ?? false,
        deleted: this.filters?.deleted ?? false,
        limit,
        offset,
      },
      { withTags: this.withTags, withKnots: this.withKnots, withConnections: false, knotsSource: this.knotsSource },
    );
  }

  public handleDoubleClickItem(sheet: Sheet) {
    this.doubleClickSheet.emit(sheet);
  }

  public handleOpenSelected(event: SelectableOpenEvent<Sheet>): void {
    if (event.source === 'removal' && this.besideEditor()) return;
    this.clickSheet.emit(event.item.data);
  }
}
